import {
	buildMatterBridgeMortgage,
	getInterestRateAbovePrime,
	getPriority,
} from '@core/util/llc';
import { dollarsToCents, stringToDollars } from '@core/util/money';
import { DATE_FORMAT } from '@models/businesses';
import {
	AdvancedAmountLabels,
	LLCPriority,
	LLCTransaction,
} from '@models/llc-mortgages';
import {
	InterestRateType,
	MatterMortgage,
	MatterMortgageType,
	NetAdvancedAmount,
	NetAmountType,
	TransactionType,
} from '@models/mortgage';
import { format, parseISO } from 'date-fns';
import { AmendmentVM, Field } from './ammendments.models';
import { Update } from '@repos/mortgages/llc.service';
import LogRocket from 'logrocket';
import { cloneDeep } from 'lodash';
import { createId } from '@core/util/file';
import { isSet } from '@core/util/value';
import { FinancialInstitutionLender } from '@models/lender';

export const updateMatterMortgage = (
	transaction: LLCTransaction,
	amendments: AmendmentVM[],
	transactionPriority: LLCPriority,
	matterMortgages: MatterMortgage[],
): Update<Partial<MatterMortgage>>[] => {
	LogRocket.log('Amendments: apply mortgage details to matter mortgage');
	const priority = getPriority(transactionPriority);
	const mainMortgage = matterMortgages.find(
		(x) => x.relatedMortgageId === transaction.id,
	);

	const bridge = matterMortgages.find(
		(x) =>
			x.transactionType === TransactionType.BRIDGE &&
			x.type === MatterMortgageType.LLC,
	);

	const partialMainMortgage: Partial<MatterMortgage> = getMappedMortgageChanges(
		amendments,
		transaction,
		mainMortgage,
	);
	const partialBridgeMortgage: Partial<MatterMortgage> =
		getMappedBridgeChanges(amendments);

	if (partialBridgeMortgage?.netAdvancedAmounts?.length > 0) {
		const original = cloneDeep(mainMortgage?.netAdvancedAmounts) ?? [];

		partialMainMortgage.netAdvancedAmounts.forEach((adv) => {
			const orig = original.find(
				(x) =>
					x.calculated === true &&
					x.type === adv.type &&
					x.description === adv.description,
			);
			if (orig) {
				orig.amount = adv.amount;
			} else {
				original.push(adv);
			}
		});

		partialMainMortgage.netAdvancedAmounts = original;
	}

	const result: Update<Partial<MatterMortgage>>[] = [];

	if (Object.keys(partialMainMortgage)?.length > 0) {
		LogRocket.log('Attempting to apply mortgage updates', partialMainMortgage);
		partialMainMortgage.netAdvancedAmounts = getAdvancedAmountChanges(
			mainMortgage?.netAdvancedAmounts,
			partialMainMortgage.netAdvancedAmounts,
		);

		if (mainMortgage?.id) {
			result.push({ id: mainMortgage.id, update: partialMainMortgage });
		} else {
			const errorMessage = `Amendments for a main mortgage were found - but no mortgage could be found to apply amendmnets to with transactionId: ${transaction.id}, priority: ${priority}`;
			LogRocket.error(errorMessage);
			throw new Error(errorMessage);
		}
	}

	if (Object.keys(partialBridgeMortgage)?.length > 0) {
		LogRocket.log(
			'Attempting to apply bridge mortgage updates',
			partialBridgeMortgage,
		);
		partialBridgeMortgage.netAdvancedAmounts = getAdvancedAmountChanges(
			bridge?.netAdvancedAmounts,
			partialBridgeMortgage.netAdvancedAmounts,
		);

		if (bridge?.id) {
			result.push({ id: bridge.id, update: partialBridgeMortgage });
		} else {
			LogRocket.info('Adding bridge to matter');
			// build new mortgage
			const newBridge = {
				...buildMatterBridgeMortgage(
					mainMortgage.relatedMortgageId,
					transaction,
				),
				...partialBridgeMortgage,
			};
			result.push({ id: createId(), update: newBridge });

			// const errorMessage =
			// 	'Amendments for a bridge mortgage were found - but no bridge mortgage could be found to apply amendmnets to';
			// LogRocket.error(errorMessage);
			// throw new Error(errorMessage);
		}
	}

	return result;
};

const getAdvancedAmountChanges = (
	originalNetAdvance: NetAdvancedAmount[],
	updatedNetAdvance: NetAdvancedAmount[],
): NetAdvancedAmount[] => {
	const original = cloneDeep(originalNetAdvance) ?? [];

	updatedNetAdvance?.forEach((adv) => {
		const index = original?.findIndex(
			(x) =>
				x.calculated === true &&
				x.type === adv.type &&
				x.description === adv.description,
		);
		if (index > -1) {
			if (isSet(adv.amount)) {
				original[index].amount = adv.amount;
			} else {
				original.splice(index, 1);
			}
		} else {
			original.push(adv);
		}
	});

	return original;
};

export const getMappedMortgageChanges = (
	amendments: AmendmentVM[],
	transaction: LLCTransaction,
	matterMortgage: MatterMortgage,
): Partial<MatterMortgage> => {
	const partialMainMortgage: Partial<MatterMortgage> = {};
	const lenderId = Object.keys(matterMortgage?.lenders ?? {})[0];
	partialMainMortgage.netAdvancedAmounts = [];
	amendments.forEach((x) => {
		switch (x?.mapping?.field) {
			case Field.LENDER_REMARKS:
				partialMainMortgage.remarks = x.newValue;
				break;
			case Field.MORTGAGE_CENTRE_ADDRESS_STREET_1:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								street: x.newValue,
							},
							mortgageeType: 'institution',
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_CENTRE_ADDRESS_STREET_2:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								street2: x.newValue,
							},
							mortgageeType: 'institution',
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_CENTRE_ADDRESS_POSTALCODE:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								postcode: x.newValue,
							},
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_CENTRE_ADDRESS_CITY:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								city: x.newValue,
							},
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_CENTRE_PHONE:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							phone: x.newValue,
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_CENTRE_FAX:
				if (transaction?.data?.serviceAddressType === 'MORTGAGE CENTER') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							fax: x.newValue,
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_STREET_1:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								street: x.newValue,
							},
							mortgageeType: 'institution',
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_STREET_2:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								street2: x.newValue,
							},
							mortgageeType: 'institution',
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_POSTALCODE:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								postcode: x.newValue,
							},
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_CITY:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							address: {
								city: x.newValue,
							},
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_PHONE:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							phone: x.newValue,
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.MORTGAGE_OWNING_BRANCH_FAX:
				if (transaction?.data?.serviceAddressType === 'OWNING BRANCH') {
					partialMainMortgage.lenders = {
						[lenderId]: {
							fax: x.newValue,
						} as FinancialInstitutionLender,
					};
				}
				break;
			case Field.PROPERTY_MORTGAGE_PRIORITY:
				partialMainMortgage.priority = getPriority(x.newValue as LLCPriority);
				break;
			case Field.MORTGAGE_MATTER_CALC_PERIOD_TEXT:
			case Field.MORTGAGE_MATTER_CALC_PERIOD_CODE:
				partialMainMortgage.interestCalculated = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_REGISTERED_AMOUNT:
				partialMainMortgage.amount = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_REG_INTEREST_RATE_TEXT:
				partialMainMortgage.interestRateAsReg = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_REGISTERED_AMOUNT_TEXT:
				partialMainMortgage.amount =
					dollarsToCents(stringToDollars(x.newValue)) ?? null;
				break;
			case Field.MORTGAGE_MATTER_PAYMENTS_DUE:
				partialMainMortgage.paymentDue = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_FIRST_PAYMENT_DATE:
				partialMainMortgage.firstPaymentDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
			case Field.MORTGAGE_MATTER_FIRST_PAYMENT_DATE_TEXT:
				partialMainMortgage.firstPaymentDate = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_INTEREST_ADJ_DATE:
				partialMainMortgage.interestAdjustmentDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
			case Field.MORTGAGE_MATTER_INTEREST_ADJ_DATE_TEXT:
				partialMainMortgage.interestAdjustmentDate = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_INTEREST_RATE:
				partialMainMortgage.interestRate = Number(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_INTEREST_RATE_TYPE:
				partialMainMortgage.interestRateType =
					x.newValue.toLowerCase() === 'fixed'
						? InterestRateType.FIXED
						: InterestRateType.VARIABLE;
				break;
			case Field.PROPERTY_LENDER_COLLECTS_PROPERTY_TAXES:
				partialMainMortgage.lenderCollectsPropertyTaxes =
					x.newValue?.toString() === 'true';
				break;
			case Field.MORTGAGE_LENDER_REFERENCE_NUMBER:
				partialMainMortgage.referenceNumber = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_MATURITY_DATE:
				partialMainMortgage.maturityDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
			case Field.MORTGAGE_MATTER_MATURITY_DATE_TEXT:
				partialMainMortgage.maturityDate = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT:
				partialMainMortgage.principalAndInterest =
					dollarsToCents(stringToDollars(x.newValue)) ?? null;
				partialMainMortgage.regularPaymentsAsReg = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_TAXES:
				partialMainMortgage.taxes = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_OTHER:
				partialMainMortgage.other = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_NET_ADVANCED:
				partialMainMortgage.loanAmount = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_NET_ADVANCED_AMOUNTS:
				partialMainMortgage.netAdvanced = dollarsToCents(x.newValue);
				partialMainMortgage.netAdvancedOverride = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_INC_ABOVE_BELOW_PRIME:
				partialMainMortgage.interestRate = Math.abs(Number(x.newValue));
				partialMainMortgage.interestAbovePrime = getInterestRateAbovePrime(
					Number(x.newValue),
				);
				break;
			case Field.MORTGAGE_MATTER_INTEREST_CALCULATED:
				partialMainMortgage.interestCalculated = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_REG_PAYMENTS:
				partialMainMortgage.regularPayments = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_TERM:
				partialMainMortgage.term = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_ASSIGNMENT_OF_RENTS:
				partialMainMortgage.assignmentOfRents =
					x.newValue?.toString() === 'true';
				break;
			case Field.MORTGAGE_MATTER_CHARGE_TERM:
				partialMainMortgage.standardChargeTerm = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_LOAN_AMOUNT:
				partialMainMortgage.loanAmount = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_PRINCIPLE_AND_INTEREST:
				partialMainMortgage.principalAndInterest = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_PAYMENT_FREQUENCY:
				partialMainMortgage.paymentFrequency = x.newValue;
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_APPRAISAL:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.APPRAISAL_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.APPRAISAL_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_HOLD_BACK:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.HOLD_BACK_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.HOLDBACK_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_INSURANCE_APPLICATION:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.INSURANCE_APP_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.INSURANCE_APPLICATION_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_INSURANCE_PREMIUM:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.INSURANCE_PREMIUM,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.INSURANCE_PREMIUM,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_LENDER:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.LENDER_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.LENDER_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_MISC:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.MISC_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.MISC_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_WIRE:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.WIRE_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.WIRE_FEE,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_INS_PREMIUM_TAX:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.TAX_INSURANCE_PREMIUM,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.TAX_INS_PREM,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_BUYDOWN:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.BUY_DOWN_COMMITMENT_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_CREDIT_LINE:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.LINE_OF_CREDIT,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_TAX_HOLDBACK:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.TAX_HOLDBACK,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_INTERNAL_PAYOUT:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.INTERNAL_MORTGAGE_PAYOUT,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_REBATE:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.REBATE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER_CREDIT,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_FCT_FEE:
				partialMainMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.FCT_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.LENDER_FEE,
					calculated: true,
				});
				break;
		}
	});

	return partialMainMortgage;
};

export const getMappedBridgeChanges = (
	amendments: AmendmentVM[],
): Partial<MatterMortgage> => {
	const partialBridgeMortgage: Partial<MatterMortgage> = {};
	amendments.forEach((x) => {
		switch (x?.mapping?.field) {
			case Field.MORTGAGE_MATTER_BRIDGE_AMOUNT:
				partialBridgeMortgage.amount = dollarsToCents(x.newValue);
				partialBridgeMortgage.loanAmount = dollarsToCents(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_LOAN_FEES_BRIDGE:
				if (!partialBridgeMortgage.netAdvancedAmounts) {
					partialBridgeMortgage.netAdvancedAmounts = [];
				}
				partialBridgeMortgage.netAdvancedAmounts.push({
					id: createId(),
					description: AdvancedAmountLabels.BRIDGE_LOAN_APP_FEE,
					amount: dollarsToCents(x.newValue),
					type: NetAmountType.OTHER,
					calculated: true,
				});
				break;
			case Field.MORTGAGE_MATTER_BRIDGE_FIRST_PAYMENT_DATE:
				partialBridgeMortgage.firstPaymentDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
			case Field.MORTGAGE_MATTER_BRIDGE_INTEREST_ADJ_DATE:
				partialBridgeMortgage.interestAdjustmentDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
			case Field.MORTGAGE_MATTER_BRIDGE_INTEREST_RATE:
				partialBridgeMortgage.interestRate = Number(x.newValue);
				break;
			case Field.MORTGAGE_MATTER_BRIDGE_MATURITY_DATE:
				partialBridgeMortgage.maturityDate =
					x.newValue?.length > 0
						? format(parseISO(x.newValue), DATE_FORMAT)
						: '';
				break;
		}
	});

	return partialBridgeMortgage;
};
